import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { UniversalSearchRequestModelInterface } from '../../../core/models/universal-search-request.model.interface';
import { LoadingService, RequestBodyService } from '../../services';
import { SavedSearches } from '../../services/saved-searches.service';
import { SaveSearchComponent } from '../save-search';
import { CommonUtilService } from '../../services/CommonUtil.service';
import { SearchService } from '../../../results/search/services/search.service';
import { SidebarTabTypeEnum } from '../../enums';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { SaveSearchApiService } from '../../services/api';
import { SavedSearchCreateUpdateResponse } from '../../dto';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, take } from 'rxjs';
import { LEGACY_THEME_CLASS } from 'src/app/shared/constants';

const LOADING_TAG: string = 'app-clear-search-loading-tag';

interface ClearSearchDialogData {
    type?: string;
}

@UntilDestroy()
@Component({
    selector: 'app-clear-search',
    templateUrl: './clear-search.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ClearSearchComponent implements OnInit {
    activeSearch: SavedSearchCreateUpdateResponse<UniversalSearchRequestModelInterface> | undefined;
    type: string | undefined;
    isSameUser: boolean = false;
    canUpdateSearch: boolean = false;
    hasExistingSavedSearch: boolean = false;

    readonly isLoading$: Observable<boolean> = this.loadingService.getIsLoading(LOADING_TAG);

    constructor(
        private saveSearchService: SavedSearches,
        private requestBodyService: RequestBodyService,
        private searchService: SearchService,
        private toastr: ToastrService,
        private commonUtilService: CommonUtilService,
        private authenticationService: AuthenticationService,
        private readonly loadingService: LoadingService,
        private readonly saveSearchApiService: SaveSearchApiService,
        private readonly matDialog: MatDialog,
        private readonly matDialogRef: MatDialogRef<ClearSearchComponent>,
        @Inject(MAT_DIALOG_DATA) private readonly data: ClearSearchDialogData
    ) {
        if (this.data.type) {
            this.type = this.data.type;
        }
        this.activeSearch = this.saveSearchService.universalActiveSearch;
        this.hasExistingSavedSearch = !!this.activeSearch;
        this.setSaveActionState();
    }

    ngOnInit(): void {
        this.authenticationService.currentUser$.pipe(take(1)).subscribe((data) => {
            this.isSameUser = !!this.activeSearch && data.id === this.activeSearch.userId;
            this.setSaveActionState();
        });
    }

    onClose(): void {
        this.matDialogRef.close();
    }

    onOpenSaveDialog(): void {
        this.onClose();
        const searchCriteria = this.searchService.searchCriteria;
        const config: MatDialogConfig = {
            panelClass: ['modal-md', LEGACY_THEME_CLASS],
            data: {
                header: 'Save Search',
                searchCriteria: searchCriteria,
                type: this.type
            }
        };
        this.matDialog.open(SaveSearchComponent, config);
    }

    onUpdateSearch(): void {
        const searchCriteria = this.searchService.searchCriteria;
        const saveSearchType = !searchCriteria ? this.searchService.searchModelData.request_type : searchCriteria.request_type === 'CONTACTS' ? 'contact' : 'company';
        this.loadingService.loadingWrapper(this.saveSearchApiService.updateSearch(String(this.activeSearch.id), searchCriteria, saveSearchType), LOADING_TAG)
        .pipe(untilDestroyed(this))
        .subscribe(
            (response) => {
                this.saveSearchService.setUniversalActiveSearch(searchCriteria);
                this.requestBodyService.changeContactSaveSearch(response);
                this.toastr.success('Search updated successfully');
                this.saveSearchService.welComeLoadedFlag.next(false);
                this.saveSearchService.emitSaveUpdateSearchModalClosed();
                this.onClose();
            },
            () => {
                this.onClose();
                this.saveSearchService.welComeLoadedFlag.next(false);
            });
    }

    onDoNotSave(): void {
        this.searchService.emitSearchPerformedEvent(false, SidebarTabTypeEnum.SALES_INTEL);
        this.commonUtilService.clearSidebarSearch();
        this.onClose();
    }

    private setSaveActionState(): void {
        this.canUpdateSearch = !!this.activeSearch && this.isSameUser && !this.activeSearch.lock;
    }
}
